import React from 'react';
import { LottoStats } from '../types';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { Flame, Snowflake, TrendingUp, Users } from 'lucide-react';
import Tooltip from './Tooltip';
import LottoBall from './LottoBall';

interface StatsDisplayProps {
  stats: LottoStats | null;
  loading: boolean;
}

const StatsDisplay: React.FC<StatsDisplayProps> = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="w-full p-6 rounded-2xl bg-slate-900 border border-slate-800 animate-pulse">
        <div className="h-6 bg-slate-800 rounded w-48 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-32 bg-slate-800 rounded"></div>
          <div className="h-32 bg-slate-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (!stats || (stats.hotNumbers.length === 0 && stats.coldNumbers.length === 0)) {
    return null;
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center gap-2 mb-2">
         <TrendingUp className="text-yellow-500" size={20} />
         <h2 className="text-xl font-bold font-['Space_Grotesk'] text-slate-200">Historical Analysis</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Hot Numbers */}
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-4 text-orange-400">
            <Flame size={18} />
            <Tooltip text="Numbers that have been drawn most frequently in recent history. Statistically, these are 'on a streak'.">
              <h3 className="font-bold text-sm uppercase tracking-wider">Hot Numbers</h3>
            </Tooltip>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.hotNumbers.map((num) => (
              <LottoBall key={`hot-${num}`} number={num} size="md" title="Hot number" />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">Frequently drawn recently</p>
        </div>

        {/* Cold Numbers */}
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-4 text-cyan-400">
            <Snowflake size={18} />
            <Tooltip text="Numbers that have rarely been drawn recently. Some players believe these are 'overdue' to be picked.">
              <h3 className="font-bold text-sm uppercase tracking-wider">Cold Numbers</h3>
            </Tooltip>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.coldNumbers.map((num) => (
              <LottoBall key={`cold-${num}`} number={num} size="md" title="Overdue/cold number" />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">Least drawn or overdue</p>
        </div>
      </div>

      {/* Frequent Pairs */}
      {stats.frequentPairs && stats.frequentPairs.length > 0 && (
         <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 backdrop-blur-sm">
           <div className="flex items-center gap-2 mb-4 text-purple-400">
             <Users size={18} />
             <Tooltip text="Pairs of numbers that statistically appear together in the same draw more often than others.">
               <h3 className="font-bold text-sm uppercase tracking-wider">Common Pairs</h3>
             </Tooltip>
           </div>
           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
             {stats.frequentPairs.map((pair, idx) => (
               <div key={idx} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700 flex items-center justify-between">
                 <div className="flex items-center gap-2">
                    <LottoBall number={pair.numbers[0]} size="sm" />
                    <span className="text-slate-500">&</span>
                    <LottoBall number={pair.numbers[1]} size="sm" />
                 </div>
                 <div className="text-xs text-slate-400 font-mono">
                    Freq: <span className="text-purple-300 font-bold">{pair.frequency || 'High'}</span>
                 </div>
               </div>
             ))}
           </div>
         </div>
      )}

      {/* Frequency Chart */}
      {stats.frequencyData.length > 0 && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 backdrop-blur-sm">
          <h3 className="font-bold text-sm text-slate-400 uppercase tracking-wider mb-6">Most Frequent Numbers — Last {stats.frequencyWindow || 50} Draws</h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.frequencyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis 
                  dataKey="number" 
                  tick={{ fill: '#94a3b8', fontSize: 12 }} 
                  axisLine={{ stroke: '#334155' }}
                  tickLine={false}
                  label={{ value: 'Number', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 10 }}
                />
                <YAxis 
                  tick={{ fill: '#94a3b8', fontSize: 12 }} 
                  axisLine={{ stroke: '#334155' }}
                  tickLine={false}
                />
                <RechartsTooltip 
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc', borderRadius: '8px' }}
                />
                <Bar dataKey="frequency" radius={[4, 4, 0, 0]}>
                  {stats.frequencyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index < 3 ? '#eab308' : '#64748b'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsDisplay;